import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useCurrentUser } from '../hooks/useUserQueries';
import { isAdmin } from '../utils/roleUtils';
import Header from '../components/layout/Header';
import ProfileHeader from '../components/profile/ProfileHeader';
import ProfileForm from '../components/profile/ProfileForm';
import BankDetails from '../components/profile/BankDetails';
import JobHistory from '../components/profile/JobHistory';
import DocumentsTab from '../components/profile/DocumentsTab';
import Transactions from '../components/profile/Transactions';
import './Profile.css';

const Profile = () => {
  const { user: authUser } = useAuth();
  const { data: currentUser, isLoading: isLoadingUser } = useCurrentUser();
  const location = useLocation();
  
  const user = currentUser || authUser;
  const [activeTab, setActiveTab] = useState('profile');

  // If user is admin and activeTab is 'history', reset to 'profile'
  useEffect(() => {
    if (isAdmin(user) && activeTab === 'history') {
      setActiveTab('profile');
    }
  }, [user, activeTab]);

  // Allow navigation to open a specific tab (e.g. Transactions) via router state
  useEffect(() => {
    if (!user) return;

    const desiredTab = location.state && location.state.openTab;
    if (!desiredTab) return;

    const availableTabs = [
      'profile',
      ...(user?.role_name === 'employee' || user?.role_name === 'employer' ? ['documents', 'bank', 'transactions'] : []),
      ...(!isAdmin(user) ? ['history'] : []),
    ];

    if (availableTabs.includes(desiredTab)) {
      setActiveTab(desiredTab);
    }
  }, [location.state, user]);


  if (isLoadingUser) {
    return (
      <div className="profile">
        <Header />
        <div className="profile__content">
          <div style={{ textAlign: 'center', padding: '2rem' }}>Loading...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="profile">
        <Header />
        <div className="profile__content">
          <div style={{ textAlign: 'center', padding: '2rem' }}>User not found</div>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'profile', label: 'Profile Information' },
    ...(user?.role_name === 'employee' || user?.role_name === 'employer' ? [{ id: 'documents', label: 'Documents' }] : []),
    ...(user?.role_name === 'employee' ? [{ id: 'bank', label: 'Bank Details' }] : []),
    ...(user?.role_name === 'employee' || user?.role_name === 'employer' ? [{ id: 'transactions', label: 'Transactions' }] : []),
    ...(!isAdmin(user) ? [{ id: 'history', label: 'Job History' }] : []),
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return <ProfileForm user={user} />;
      case 'documents':
        return <DocumentsTab user={user} />;
      case 'bank':
        return <BankDetails />;
      case 'transactions':
        return <Transactions />;
      case 'history':
        return <JobHistory />;
      default:
        return <ProfileForm user={user} />;
    }
  };

  return (
    <div className="profile">
      <Header />
      
      <div className="profile__content">
        <div className="profile__container">
          <ProfileHeader user={user} />

          <div className="profile__tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`profile__tab ${activeTab === tab.id ? 'profile__tab--active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="profile__tab-content">
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;

